/**
 * A compact Attribute / [Space] / Value grid built on `@jsq/ds` `Grid` (AG Grid),
 * mirroring the design's MiniAttrGrid. Read-only cells show a value; editable
 * cells opt into inline editing (`editable` + `editor`) via AG Grid's built-in
 * cell editors (select / number / text), a native date input, and a Multi-select
 * dropdown. First click starts the editor (closed field + row highlight); the
 * next click opens the list — the same as Single-select's `agSelectCellEditor`.
 * System-derived cells (`derived`) are locked, and required-but-empty editable
 * cells surface a "fill me in" CTA — all matching the design's `.mag-*` cell
 * class rules.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid, AgGrid } from "@jsq/ds/core";
import { joinMultiValue, splitMultiValueAgainstOptions } from "#helpers/multi-select-value";
import { DATE_FORMAT_TOOLTIP, DATE_REPLACE_TOOLTIP, NO_VALUE_TOOLTIP } from "./attributeValues";
import * as g from "./MiniAttrGrid.css";

/** Params a cell editor receives from the grid (AG Grid's own editor contract). */
export type MiniAttrCellEditorParams = AgGrid.ICellEditorParams<MiniAttrRow, string>;

/**
 * A caller-supplied cell editor, mounted as an AG Grid POPUP so a control with its
 * own dropdown (a typeahead) isn't clipped by the row.
 *
 * Must be a `forwardRef` exposing `getValue()`: a component reached through
 * `cellEditorSelector` is given legacy params plus a ref, NOT the newer
 * `CustomCellEditorProps` / `onValueChange` shape — a plain function component
 * fails at runtime with "Function components cannot be given refs".
 */
export type MiniAttrCellEditor = React.ForwardRefExoticComponent<
  MiniAttrCellEditorParams & React.RefAttributes<AgGrid.ICellEditor>
>;

export type MiniAttrEditor =
  | { kind: "select"; options: string[] }
  | { kind: "multi-select"; options: string[] }
  | { kind: "text" | "date" | "number" }
  | { kind: "component"; component: MiniAttrCellEditor };

export type MiniAttrRow = {
  /** Stable id + the key passed back to onEdit (attribute key or local name). */
  key: string;
  scope: "global" | "local";
  attribute: string;
  space?: string;
  value: string;
  /** Inline-editable (false for system-derived / read-only rows). */
  editable?: boolean;
  editor?: MiniAttrEditor;
  /** System-derived value (Client ID, Document Status) — read-only with a lock. */
  derived?: boolean;
  /** Required attribute — an empty editable cell prompts the user to fill it in. */
  flag?: boolean;
  /**
   * Backend option id for a `select` editor's options, keyed by display value.
   * Opaque to this grid — the caller uses it to resolve a picked value back to
   * the id its persistence layer needs (e.g. `valueIds`, DV-466/DV-544).
   */
  optionIds?: Record<string, string>;
};

const isEmpty = (v: unknown) => v == null || v === "";

/** Native `<input type="date">` only accepts a calendar day. */
const LEADING_DAY = /^(\d{4}-\d{2}-\d{2})/;

export const toDateInputValue = (value: unknown): string => {
  if (value == null || value === "") return "";
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is an opaque grid cell value, defensively stringified for date parsing
  return LEADING_DAY.exec(String(value))?.[1] ?? "";
};

/**
 * Native date picker for a date-typed Value cell.
 *
 * AG Grid's `agDateStringCellEditor` asks `DataTypeService.getDateParserFunction`
 * for this column. The Value column holds mixed types (text / number / date /
 * select), so it is inferred as `text` and that parser is undefined — clicking
 * the cell throws `getDateParserFunction(...) is not a function`.
 *
 * Emptying a date that has one is NOT committed (DV-705): a date replaces a date.
 * See the `isCancelAfterEnd` note below for why.
 */
export const DateStringCellEditor = forwardRef<AgGrid.ICellEditor, MiniAttrCellEditorParams>(
  (params, ref) => {
    const original = toDateInputValue(params.value);
    const latest = useRef(original);
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(
      ref,
      () => ({
        getValue: () => latest.current,
        afterGuiAttached: () => inputRef.current?.focus(),
        /**
         * Emptying a date that already had one is not an edit — the cell keeps
         * its date and the user has to pick a replacement (DV-705).
         *
         * The backend `Date` scalar has no empty form, so a cleared date can't be
         * persisted at all: the drawer staged the clear, blocked Save on it, and
         * left the only way out through a cell that now looked untouched — which
         * is how "cleared the date, can't put one back" got reported. Refusing the
         * clear here, where the value is still recoverable, is the alternative to
         * staging something no Save can accept.
         *
         * The same refusal covers the native picker's own clear affordance and an
         * accidental one — opening the editor on a value the input can't show
         * (see {@link toDateInputValue}) and closing it reads as an emptied date.
         *
         * A cell that was ALREADY empty is untouched by this: `original` is `""`,
         * so an unpicked date commits `""` over `""`, which AG Grid treats as no
         * change, and adding a first date still commits normally.
         */
        isCancelAfterEnd: () => original !== "" && latest.current === "",
      }),
      [original],
    );
    return (
      <input
        ref={inputRef}
        type="date"
        className={g.dateInput}
        defaultValue={latest.current}
        aria-label={params.data?.attribute ?? "Date"}
        onChange={(e) => {
          latest.current = e.target.value;
        }}
      />
    );
  },
);
DateStringCellEditor.displayName = "DateStringCellEditor";

const multiSelectOptions = (params: MiniAttrCellEditorParams): string[] =>
  params.data?.editor?.kind === "multi-select" ? params.data.editor.options : [];

/** Keep AG Grid from blur-committing before click/keyboard handlers run. */
const keepEditorOpen = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const joinSelected = (selected: Set<string>): string => joinMultiValue([...selected]);

const optionIndexForKey = (key: string, current: number, count: number): number | null => {
  if (count === 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowDown") return current < 0 ? 0 : Math.min(count - 1, current + 1);
  if (key === "ArrowUp") return current < 0 ? count - 1 : Math.max(0, current - 1);
  return null;
};

/**
 * Multi-select Value cell editor.
 *
 * First click (AG Grid starting the editor) mounts a closed field so the row
 * highlights like `agSelectCellEditor`. The next click (or Enter/Space) opens
 * an in-flow list with a max height and `overflow-y: auto` — DS `Select`'s own
 * menu is `position: absolute` and gets clipped by the 38px popup, so it cannot
 * scroll. `preventDefault` on mousedown keeps a blur-commit from beating the
 * click; `stopPropagation` on wheel keeps the details drawer from stealing
 * the scroll. Arrow keys move between options. Escape closes the list and is
 * captured on `window` so it cannot discard staged edits or close the overlay.
 */
export const MultiSelectCellEditor = forwardRef<AgGrid.ICellEditor, MiniAttrCellEditorParams>(
  (params, ref) => {
    const options = multiSelectOptions(params);
    const selectedRef = useRef(
      new Set(splitMultiValueAgainstOptions(params.value ?? undefined, options)),
    );
    const [selected, setSelected] = useState(() => selectedRef.current);
    const [open, setOpen] = useState(false);
    const fieldRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const optionEls = useRef<Array<HTMLButtonElement | null>>([]);
    const extraSelected = [...selected].filter((label) => !options.includes(label));
    const listOptions = [...options, ...extraSelected];
    const display = joinSelected(selected);
    const fieldWidth = params.eGridCell?.offsetWidth;

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => joinSelected(selectedRef.current),
        isPopup: () => true,
      }),
      [options],
    );

    useEffect(() => {
      if (!open) return;
      listRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setOpen(false);
        fieldRef.current?.focus();
      };
      window.addEventListener("keydown", onKey, true);
      return () => window.removeEventListener("keydown", onKey, true);
    }, [open]);

    const toggle = (option: string) => {
      const next = new Set(selectedRef.current);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      selectedRef.current = next;
      setSelected(next);
    };

    const onEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!open) return;
      const current = optionEls.current.findIndex((el) => el === document.activeElement);
      const next = optionIndexForKey(e.key, current, listOptions.length);
      if (next == null) return;
      e.preventDefault();
      optionEls.current[next]?.focus();
    };

    return (
      <div className={g.multiSelectWrap} style={fieldWidth ? { width: fieldWidth } : undefined}>
        <button
          ref={fieldRef}
          type="button"
          className={g.multiSelectValue}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={params.data?.attribute ?? "Options"}
          onMouseDown={keepEditorOpen}
          onClick={() => setOpen((isOpen) => !isOpen)}
        >
          {display}
        </button>
        {open && (
          <div
            ref={listRef}
            className={g.multiSelectPopup}
            role="listbox"
            tabIndex={0}
            aria-multiselectable
            aria-label={params.data?.attribute ?? "Options"}
            onMouseDown={keepEditorOpen}
            onWheel={(e) => e.stopPropagation()}
            onKeyDown={onEditorKeyDown}
          >
            {listOptions.map((option, index) => {
              const checked = selected.has(option);
              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  ref={(el) => {
                    optionEls.current[index] = el;
                  }}
                  className={
                    checked
                      ? `${g.multiSelectOption} ${g.multiSelectOptionSelected}`
                      : g.multiSelectOption
                  }
                  onMouseDown={keepEditorOpen}
                  onClick={() => toggle(option)}
                >
                  {option}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
MultiSelectCellEditor.displayName = "MultiSelectCellEditor";

/**
 * The option list handed to `agSelectCellEditor`, always containing the cell's
 * CURRENT value.
 *
 * AG Grid's select editor silently adopts `values[0]` whenever the cell value is
 * not one of `values` (`SelectCellEditor.init`), and this grid's `singleClickEdit`
 * + `stopEditingWhenCellsLoseFocus` mean one click on the cell and one click away
 * is enough to COMMIT that fallback. So merely opening an empty select — or one
 * holding a multi-select's combined value, which is no single option — picked the
 * first option and reported an edit nobody made (DV-607).
 *
 * Carrying the current value into the list makes the editor start on it instead,
 * and AG Grid skips the change event entirely when a value comes back unchanged.
 */
export const selectEditorValues = (options: string[], value: unknown): string[] => {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is an opaque grid cell value, defensively stringified for display
  const current = value == null ? "" : String(value);
  return options.includes(current) ? options : [current, ...options];
};

/* Editable-but-empty cells render blank so the CTA (drawn in CSS) shows; every
   other empty cell renders an em-dash. */
const valFmt = (p: { data?: MiniAttrRow; value: unknown }) => {
  if (p.data?.editable && isEmpty(p.value)) return "";
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- p.value is an opaque grid cell value, defensively stringified for display
  return isEmpty(p.value) ? "—" : String(p.value);
};

export function MiniAttrGrid({
  rows,
  showSpace,
  onEdit,
}: {
  rows: MiniAttrRow[];
  showSpace?: boolean;
  onEdit?: (row: MiniAttrRow, value: string) => void;
}) {
  const hasEdit = rows.some((r) => r.editable);

  const sideBar = useMemo<AgGrid.SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
      ],
      defaultToolPanel: undefined,
    }),
    [],
  );

  const valueCol: AgGrid.ColDef<MiniAttrRow> = {
    headerName: "Value",
    field: "value",
    flex: showSpace ? 1.1 : 1.25,
    minWidth: showSpace ? 100 : 110,
    cellClass: g.magV,
    sortable: false,
    /**
     * One column, every attribute type. AG Grid otherwise INFERS a `cellDataType`
     * for the column from the first row's value and then REJECTS any edit whose
     * committed value doesn't match it ("Data type of the new value does not match
     * the cell data type of the column") — silently, before `onCellValueChanged`
     * ever fires. Every row here carries a string, so the column infers `text`,
     * and `agNumberCellEditor` commits a NUMBER: that is exactly how a Number
     * attribute's edit was discarded on blur (DV-603). A first row that happens to
     * read `YYYY-MM-DD` infers `dateString` and takes the text rows down the same
     * way. The column's own value is authoritative here — it is already formatted
     * by `valFmt` and re-parsed by the caller — so opt out of inference entirely.
     */
    cellDataType: false,
    valueFormatter: valFmt,
    // A date cell has no placeholder to carry the format, so the hint rides on the
    // cell's tooltip instead (DV-563) — and once it holds a date, the hint also
    // carries the rule that only another date can replace it (DV-705). Read-only
    // empty cells explain their "—" (DV-504) — every row here is bound to the
    // space by construction, so "No value set" is always the right reading
    // (never "not applicable").
    tooltipValueGetter: (p) => {
      if (p.data?.editable && p.data.editor?.kind === "date")
        return isEmpty(p.value) ? DATE_FORMAT_TOOLTIP : DATE_REPLACE_TOOLTIP;
      if (!p.data?.editable && isEmpty(p.value)) return NO_VALUE_TOOLTIP;
      return undefined;
    },
    editable: (p) => !!p.data?.editable,
    cellEditorSelector: (p) => {
      const e = p.data?.editor;
      // Popup + `popupPosition: "under"` so a typeahead's own dropdown has room
      // below the cell instead of being clipped inside the 38px row.
      if (e?.kind === "component")
        return { component: e.component, popup: true, popupPosition: "under" };
      if (e?.kind === "select")
        return {
          component: "agSelectCellEditor",
          params: { values: selectEditorValues(e.options, p.value) },
        };
      if (e?.kind === "multi-select")
        return { component: MultiSelectCellEditor, popup: true, popupPosition: "over" };
      if (e?.kind === "number") return { component: "agNumberCellEditor" };
      if (e?.kind === "date") return { component: DateStringCellEditor };
      return { component: "agTextCellEditor" };
    },
    cellClassRules: {
      [g.magVMissing]: (p) => !!p.data?.editable && !!p.data?.flag && isEmpty(p.value),
      [g.magVEdit]: (p) => !!p.data?.editable,
      [g.magVDerived]: (p) => !!p.data?.derived,
      [g.magVProblem]: (p) => !!p.data?.flag && !p.data?.editable && !isEmpty(p.value),
    },
  };

  const columnDefs: AgGrid.ColDef<MiniAttrRow>[] = showSpace
    ? [
        {
          headerName: "Attribute",
          field: "attribute",
          flex: 1,
          minWidth: 96,
          cellClass: g.magK,
          sortable: false,
        },
        {
          headerName: "Space",
          field: "space",
          flex: 1,
          minWidth: 120,
          cellClass: g.magSp,
          sortable: false,
          valueFormatter: valFmt,
        },
        valueCol,
      ]
    : [
        {
          headerName: "Attribute",
          field: "attribute",
          flex: 1,
          minWidth: 96,
          cellClass: g.magK,
          sortable: false,
        },
        valueCol,
      ];

  return (
    <div className={g.wrap}>
      <Grid<MiniAttrRow>
        rowData={rows}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        rowHeight={38}
        headerHeight={36}
        singleClickEdit
        stopEditingWhenCellsLoseFocus
        suppressCellFocus={!hasEdit}
        sideBar={sideBar}
        popupParent={typeof document !== "undefined" ? document.body : undefined}
        defaultColDef={{
          resizable: true,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          menuTabs: [],
        }}
        onCellValueChanged={(e) =>
          e.data && onEdit?.(e.data, e.newValue == null ? "" : String(e.newValue))
        }
      />
    </div>
  );
}
